import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import desktopAgentIcon from '@easytools-assets/desktop-agent/desktop-agent.svg';
import { DESKTOP_AGENT_STATE_LABEL, useDesktopAgent } from '../../contexts/DesktopAgentContext';
import { languageLabel } from '../../constants/languages';
import { AgentPhase, useElapsed } from './useAgentRun';
import { ToolContentSlot, slotStatus } from './contentModel';

type GenerateMode = 'improve' | 'rewrite';

const MODES: { id: GenerateMode; label: string; hint: string }[] = [
    { id: 'improve', label: 'Modifica la pagina attuale', hint: 'Applica il prompt al contenuto che c’è, tenendo ciò che non nomina.' },
    { id: 'rewrite', label: 'Crea da zero', hint: 'Una bozza nuova dal prompt e dai dati del tool.' },
];

const PHASES: { id: Exclude<AgentPhase, 'idle'>; label: string }[] = [
    { id: 'prepare', label: 'Preparo la richiesta' },
    { id: 'running', label: 'Il Desktop Agent scrive' },
    { id: 'checking', label: 'Controllo il risultato e salvo la bozza' },
];

const elapsedLabel = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** Riga di stato compatta del Desktop Agent: icona colorata quando è pronto, e cosa fare quando non lo è. */
export const AgentStatusLine: React.FC = () => {
    const agent = useDesktopAgent();
    const cli = agent.readOnlyAgents.find(a => a.code === agent.agent)?.displayName;
    return (
        <div className={`tcs-agent is-${agent.state}`}>
            <img src={desktopAgentIcon} alt="" aria-hidden="true" />
            <span>
                {agent.state === 'ready' ? `Pronto con ${cli}, in sola lettura` : DESKTOP_AGENT_STATE_LABEL[agent.state]}
                {agent.state !== 'ready' && agent.state !== 'checking' && <> · <Link to="/desktop-agent">come attivarlo</Link></>}
            </span>
        </div>
    );
};

/** Avanzamento di una richiesta al Desktop Agent, con annulla. */
export const AgentProgress: React.FC<{ phase: AgentPhase; startedAt: number | null; lastLine: string; onCancel: () => void; label?: string }> =
    ({ phase, startedAt, lastLine, onCancel, label }) => {
        const elapsed = useElapsed(startedAt);
        const current = PHASES.findIndex(p => p.id === phase);
        return (
            <div className="tcs-progress" role="status" aria-live="polite">
                {label && <strong className="tcs-progress__label">{label}</strong>}
                <ol>
                    {PHASES.map((p, i) => (
                        <li key={p.id} className={i < current ? 'is-done' : i === current ? 'is-now' : undefined}>
                            <span className="tcs-progress__mark" aria-hidden="true">{i < current && <i className="bi bi-check-lg" />}</span>
                            {p.label}
                            {i === current && p.id === 'running' && <span className="tcs-progress__time">{elapsedLabel(elapsed)}</span>}
                        </li>
                    ))}
                </ol>
                {lastLine && <p className="tcs-progress__line">{lastLine}</p>}
                <button type="button" className="btn btn-sm btn-light align-self-start" onClick={onCancel}>Annulla</button>
            </div>
        );
    };

/**
 * Pannello "Genera con il Desktop Agent": il prompt lo scrive l'amministratore e si salva
 * con la pagina, così si può rigenerare o correggere. L'agent non legge il codice.
 */
export const GeneratePanel: React.FC<{
    hasContent: boolean;
    title: string;
    phase: AgentPhase;
    startedAt: number | null;
    lastLine: string;
    /** Prompt salvato con la pagina (page.agentNotes). */
    prompt: string;
    maxLength: number;
    onPromptChange: (prompt: string) => void;
    onGenerate: (mode: GenerateMode) => void;
    onCancel: () => void;
}> = ({ hasContent, title, phase, startedAt, lastLine, prompt, maxLength, onPromptChange, onGenerate, onCancel }) => {
    const agent = useDesktopAgent();
    const [mode, setMode] = useState<GenerateMode>(hasContent ? 'improve' : 'rewrite');
    const id = useId();
    const modes = hasContent ? MODES : MODES.filter(m => m.id === 'rewrite');
    // Il pulsante resta attivo: al clic si spiega cosa manca.
    const [notReady, setNotReady] = useState(false);
    const tooLong = prompt.length > maxLength;

    if (phase !== 'idle')
        return <AgentProgress phase={phase} startedAt={startedAt} lastLine={lastLine} onCancel={onCancel} label={title} />;

    const submit = () => {
        if (agent.state !== 'ready') { setNotReady(true); void agent.refresh(); return; }
        if (tooLong) { document.getElementById(id)?.focus(); return; }
        setNotReady(false);
        onGenerate(mode);
    };

    return (
        <div className="tcs-gen">
            <AgentStatusLine />
            <div className="tcs-fields__field mb-0">
                <div className="tcs-fields__labelrow">
                    <label className="tcs-fields__label" htmlFor={id}>Prompt</label>
                    <span className={`tcs-count${tooLong ? ' is-over' : ''}`}>{prompt.length} / {maxLength}</span>
                </div>
                <textarea id={id} className="form-control form-control-sm tcs-gen__prompt" rows={9} value={prompt}
                    placeholder={'Cosa deve raccontare la pagina, a chi si rivolge, il tono.\nEsempio: scrivi la pagina pubblica per i responsabili commerciali. Il tool importa i listini da Excel, mostra le variazioni di prezzo prima di salvarle e aggiorna Dynamics in un colpo solo. Chiudi con un invito a provarlo.'}
                    aria-describedby={`${id}-hint`} aria-invalid={tooLong}
                    onChange={e => onPromptChange(e.target.value)} />
                <div id={`${id}-hint`} className={tooLong ? 'tcs-fields__error' : 'tcs-fields__hint'}>
                    {tooLong
                        ? `Accorcialo di ${prompt.length - maxLength} caratteri.`
                        : 'Si salva con la bozza. Il Desktop Agent non legge il codice: scrive con quello che gli dici qui, più nome, descrizione e tipo del tool.'}
                </div>
            </div>
            {modes.length > 1 && (
                <fieldset className="tcs-gen__modes">
                    <legend className="tcs-fields__label">Cosa fare</legend>
                    {modes.map(m => (
                        <label key={m.id} className={`tcs-gen__mode${mode === m.id ? ' is-on' : ''}`}>
                            <input type="radio" name="tcs-gen-mode" className="form-check-input" checked={mode === m.id} onChange={() => setMode(m.id)} />
                            <span><strong>{m.label}</strong><span>{m.hint}</span></span>
                        </label>
                    ))}
                </fieldset>
            )}
            <button type="button" className="btn btn-primary tcs-gen__go" onClick={submit}>
                {mode === 'improve' ? 'Modifica con il prompt' : 'Genera la bozza'}
            </button>
            {notReady && agent.state !== 'ready' ? (
                <p className="tcs-fields__error mb-0" role="alert">
                    Il Desktop Agent non è pronto ({DESKTOP_AGENT_STATE_LABEL[agent.state].toLowerCase()}): avvialo su questo PC
                    e controlla l’icona in alto a destra. <Link to="/desktop-agent">Come attivarlo</Link>
                </p>
            ) : (
                <p className="tcs-fields__hint mb-0">Il risultato diventa una bozza: niente va online finché non pubblichi. Puoi annullarlo con Ctrl+Z.</p>
            )}
        </div>
    );
};

/** Pannello "Traduci dalla versione italiana". */
export const TranslatePanel: React.FC<{
    slots: ToolContentSlot[];
    languages: string[];
    referenceVersion: number | null;
    phase: AgentPhase;
    startedAt: number | null;
    lastLine: string;
    current: string | null;
    onTranslate: (languages: string[]) => void;
    onCancel: () => void;
}> = ({ slots, languages, referenceVersion, phase, startedAt, lastLine, current, onTranslate, onCancel }) => {
    const agent = useDesktopAgent();
    const targets = languages.filter(l => l !== 'it');
    const [notReady, setNotReady] = useState(false);
    const [picked, setPicked] = useState<string[]>(() =>
        targets.filter(l => { const s = slots.find(x => x.language === l); return !s || s.isOutdated || (!s.published && !s.draft); }));

    if (phase !== 'idle')
        return <AgentProgress phase={phase} startedAt={startedAt} lastLine={lastLine} onCancel={onCancel}
            label={current ? `Traduco in ${languageLabel(current)}` : undefined} />;

    if (!referenceVersion)
        return <p className="tcs-fields__note">Pubblica prima la versione italiana: le traduzioni partono da quella.</p>;

    return (
        <div className="tcs-gen">
            <AgentStatusLine />
            <p className="tcs-fields__note">Parte dalla versione italiana pubblicata (v{referenceVersion}). Ogni lingua diventa una bozza da rivedere; una bozza già aperta in quella lingua viene sostituita.</p>
            <ul className="tcs-tr">
                {targets.map(l => {
                    const status = slotStatus(slots.find(s => s.language === l));
                    return (
                        <li key={l}>
                            <label>
                                <input type="checkbox" className="form-check-input" checked={picked.includes(l)}
                                    onChange={e => setPicked(prev => (e.target.checked ? [...prev, l] : prev.filter(x => x !== l)))} />
                                <span>{languageLabel(l)}</span>
                                <span className={`tcs-status tcs-status--${status.tone}`}>{status.label}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
            <button type="button" className="btn btn-primary tcs-gen__go" disabled={!picked.length}
                onClick={() => {
                    if (agent.state !== 'ready') { setNotReady(true); void agent.refresh(); return; }
                    onTranslate(targets.filter(l => picked.includes(l)));
                }}>
                {picked.length === 1 ? 'Traduci 1 lingua' : `Traduci ${picked.length} lingue`}
            </button>
            {notReady && agent.state !== 'ready' && (
                <p className="tcs-fields__error mb-0" role="alert">
                    Il Desktop Agent non è pronto: avvialo su questo PC e controlla l’icona in alto a destra. <Link to="/desktop-agent">Come attivarlo</Link>
                </p>
            )}
        </div>
    );
};

/** "Chiedi al Desktop Agent" in cima al pannello di un blocco. */
export const AskBlock: React.FC<{ busy: boolean; onAsk: (request: string) => void }> = ({ busy, onAsk }) => {
    const agent = useDesktopAgent();
    const [text, setText] = useState('');
    const id = useId();
    const ready = agent.state === 'ready';
    const send = (value: string) => { if (value.trim() && ready && !busy) onAsk(value.trim()); };
    return (
        <div className="tcs-ask">
            <label className="tcs-fields__label" htmlFor={id}>
                <img src={desktopAgentIcon} alt="" aria-hidden="true" className={ready ? '' : 'is-off'} />
                Chiedi al Desktop Agent
            </label>
            <div className="tcs-ask__row">
                <input id={id} className="form-control form-control-sm" placeholder="Più breve, tono più diretto" value={text}
                    onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); send(text); } }}
                    disabled={!ready || busy} />
                <button type="button" className="btn btn-sm btn-primary" disabled={!ready || busy || !text.trim()} onClick={() => send(text)}>Riscrivi</button>
            </div>
            <div className="tcs-ask__chips">
                {['Più breve', 'Tono più diretto', 'Aggiungi un esempio'].map(chip => (
                    <button key={chip} type="button" className="tcs-ask__chip" disabled={!ready || busy} onClick={() => send(chip)}>{chip}</button>
                ))}
            </div>
            {!ready && <span className="tcs-fields__hint">Disponibile quando il Desktop Agent è pronto.</span>}
        </div>
    );
};
